import React, { useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import ReactPaginate from 'react-paginate';
import { useRouter } from 'next/router';
import analyticsTrack from '@lib/analytics/analyticsTrack';
import getVendorVariantAnalyticsPayload
  from '@lib/vendorVariant/getVendorVariantAnalyticsPayload';
import Loader from '@components/shared/Loader/Loader';
import { animateScroll as scroll } from 'react-scroll';
import * as querystring from 'querystring';
import ProductCard from '@components/shared/ProductCardV2/ProductCard';
import ScrollToTopButton
  from '@components/pages/ShopV3/components/ScrollToTopButton/ScrollToTopButton';
import { useMaxBreakpoint } from '@hooks/useMediaQuery/useMediaQuery';
import classNames from 'classnames';
import { hashCode } from '@lib/string';
import { ISearchCriteria, ISearchFilters, ISearchResults } from '../../model/SearchResults';
import styles from './ProductList.module.scss';
import { IFeaturedItem } from '../../model/ShopPage';
import FeaturedItem from '../FeaturedItem/FeaturedItem';
import ShopSuggestions from '../ShopSuggestions/ShopSuggestions';
import { IFooterImages } from '../ShopSuggestions/formatShopSuggestions';

interface IProductListProps {
  className?: string;
  title?: string;
  searchCriteria: ISearchCriteria;
  filters: ISearchFilters;
  searchResults: ISearchResults;
  featuredItems?: IFeaturedItem[];
  footerImages: IFooterImages[];
}

const SCROLL_DURATION_MILLIS = 750;
const MAX_PAGE_COUNT = 21;

const getAnalyticsFilters = (searchCriteria: any) => {
  const filters: any[] = [];
  const CRITERIA_TO_OMIT = [
    'pageSize',
    'page',
    'facets',
    'sort'
  ];

  if (!searchCriteria) {
    return filters;
  }

  Object.keys(searchCriteria)
    .filter((criteria) => !CRITERIA_TO_OMIT.includes(criteria))
    .forEach((criteria) => filters.push({ type: criteria, value: searchCriteria[criteria] }));

  return filters;
};

const getAnalyticsSort = (sort?: string) => {
  if (sort) {
    const sortArr = sort.split('_');
    if (sortArr.length === 2) {
      return [{
        type: sortArr[0],
        value: sortArr[1]
      }];
    }
  }
  return [];
};

const getListName = (filters: ISearchFilters) => {
  if (filters?.selectedShopGroupChain?.length) {
    return filters.selectedShopGroupChain[filters?.selectedShopGroupChain?.length - 1]?.title;
  }

  if (filters.selectedMerchGroup) {
    return filters.selectedMerchGroup.title;
  }

  return '';
};

const ProductList = ({
  className = undefined,
  filters,
  searchCriteria,
  searchResults,
  featuredItems = [],
  footerImages
}: IProductListProps) => {
  const router = useRouter();

  const { pageCount, isLastPage } = useMemo((): any => {
    const numPages = Math.min(
      MAX_PAGE_COUNT,
      Math.ceil(Math.max(1, searchResults.total / searchResults.pageSize))
    );
    return ({
      pageCount: numPages,
      isLastPage: searchResults.page === numPages
    });
  }, [searchResults.total, searchResults.pageSize, searchResults.page]);

  const [backToTopVisible, setBackToTopVisible] = useState(false);
  const [analyticsListId, setAnalyticsListId] = useState('');

  const isMobile = useMaxBreakpoint('small-tablet');

  function shopPageUrl(page: number) {
    return buildShopPageUrl(router.asPath, page);
  }

  const handlePageChange = (page: number) => {
    if (page !== searchResults.page) {
      router.push(shopPageUrl(page));
    }
  };

  // Watch for route change events so we can show a spinner
  // while we're loading the next page of results
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const handleStart = (url: string) => {
      if (url !== router.asPath) {
        setLoading(true);
        scroll.scrollToTop({ duration: SCROLL_DURATION_MILLIS });
      }
    };
    const handleComplete = () => setLoading(false);

    router.events.on('routeChangeStart', handleStart);
    router.events.on('routeChangeComplete', handleComplete);
    router.events.on('routeChangeError', handleComplete);

    const newAnalyticsListId = uuidv4();
    setAnalyticsListId(newAnalyticsListId);
    analyticsTrack(
      'product_list_viewed',
      {
        filters: JSON.stringify(getAnalyticsFilters(searchCriteria)),
        listId: newAnalyticsListId,
        listName: getListName(filters),
        listType: 'shop',
        page: searchResults.page,
        products: JSON.stringify(searchResults.hits.map(
          ({ vendorVariant }) => getVendorVariantAnalyticsPayload(vendorVariant as any),
        )),
        sort: JSON.stringify(getAnalyticsSort(searchCriteria?.sort))
      },
    );

    return () => {
      router.events.off('routeChangeStart', handleStart);
      router.events.off('routeChangeComplete', handleComplete);
      router.events.off('routeChangeError', handleComplete);
    };
  }, [router.asPath]);

  const onWindowScroll = () => {
    const SCROLL_PERCENT = 0.50;
    if (window.scrollY > (window.innerHeight * SCROLL_PERCENT) && !backToTopVisible) {
      setBackToTopVisible(true);
    } else if (window.scrollY < (window.innerHeight * SCROLL_PERCENT) && backToTopVisible) {
      setBackToTopVisible(false);
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', onWindowScroll);

    return () => {
      window.removeEventListener('scroll', onWindowScroll);
    };
  });

  const featuredRowRepeat = isMobile ? 4 : 3;

  const items = useMemo(() => {
    const page = Math.max(0, searchResults.page - 1);
    const featuredItemsToDisplay = Math.floor(searchResults.hits.length / (featuredRowRepeat * 2));
    const toRepeat = featuredItemsToDisplay > 3 ? 3 : featuredItemsToDisplay;
    return featuredItems.filter((item) => item.image)
      .slice(page * toRepeat, page * toRepeat + toRepeat);
  }, [featuredItems, searchResults.page]);

  return loading ? (
    <div className={classNames(className, styles.Loader)}>
      <Loader />
    </div>
  ) : (
    <div className={className}>
      <div className={styles.Products}>
        {
          items.map((item, index) => (
            <FeaturedItem
              key={hashCode(item.title)}
              item={item}
              imagePosition={index % 2 === 0 ? 'left' : 'right'}
              style={{
                gridRow: (index + 1) * featuredRowRepeat,
                gridColumn: '1 / -1',
              }}
            />
          ))
        }
        {
          searchResults.hits.map(({ vendorVariant }, index) => {
            return (
              <ProductCard
                key={vendorVariant.id}
                listPosition={index}
                product={vendorVariant}
                analytics={{
                  location: 'shop',
                  listId: analyticsListId,
                }}
                showSaleBanner
                isShopProductCard
                showMaxDiscountText
              />
            );
          })
        }
      </div>
      {pageCount > 1 ? (
        <div className={styles.PaginationContainer}>
          <ReactPaginate
            pageCount={pageCount}
            initialPage={searchResults.page - 1}
            breakLabel={'...'}
            previousLabel="Previous"
            nextLabel="Next"
            marginPagesDisplayed={1}
            pageRangeDisplayed={3}
            containerClassName={styles.Pagination}
            activeClassName={styles.Active}
            disabledClassName={styles.Disabled}
            pageClassName={styles.PaginationItem}
            nextClassName={styles.NextPage}
            previousClassName={styles.PrevPage}
            breakClassName={styles.Break}
            hrefBuilder={(page) => shopPageUrl(page)}
            onPageChange={({ selected }) => handlePageChange(selected + 1)}
          />
        </div>
      ) : null }

      <ScrollToTopButton
        className={styles.BackToTop}
        visible={backToTopVisible}
        onClick={() => scroll.scrollToTop({ duration: SCROLL_DURATION_MILLIS })}
      />
      {isLastPage && (
        <ShopSuggestions
          className={styles.ShopSuggestions}
          footerImages={footerImages}
        />
      )}
    </div>
  );
};

export default ProductList;

function buildShopPageUrl(currentUrl: string, page: number) {
  const [path, queryString] = currentUrl.split('?');
  const query = querystring.parse(queryString);
  query.page = page.toString();
  return `${path}?${querystring.stringify(query)}`;
}
